/** What `POST /v1/auth/login` and `/refresh-token` return. Mirrors `schemas/user_schema.py`. */
export interface AuthTokens {
  access_token: string
  refresh_token: string
  token_type: string
  first_time_login?: boolean
}

/** One way somebody signs in. */
export interface AuthIdentity {
  type: string
  value: string
  verified: boolean
}

/**
 * Who is signed in. Mirrors `UserRead`.
 *
 * `roles` is resolved by the API through `role_crud`, so a user with no rows reads as
 * `['user']` rather than as an empty list — the implicit default lives there and every
 * reader goes through it. **Do not treat an empty array as "no permissions"**; treat it as
 * a response that has not arrived.
 */
export interface AuthUser {
  id: string
  name?: string | null
  last_name?: string | null
  position?: string | null
  /** Their own telephone number. On `UserRead` since `PATCH /auth/me` landed — somebody who
   *  changes their own number has to be told what it now is. */
  phone?: string | null
  language: string
  status_in_system: string
  last_login?: string | null
  identities: AuthIdentity[]
  roles: string[]
}

/** Every error body from `app_api` carries these four keys. Contract, not convention. */
export interface ApiErrorBody {
  code: string
  title: string
  /** The human-readable line. **This is the key to render.** */
  detail: string
  data: unknown
}

/**
 * A failed request.
 *
 * `detail` is read straight off the body. `app_api` guarantees it is a string on every
 * error class — including an unmatched route and a validation failure, whose per-field
 * list it moves into `data.errors` for exactly this reason.
 */
export class ApiError extends Error {
  readonly status: number
  readonly body: ApiErrorBody | null

  constructor(status: number, body: ApiErrorBody | null) {
    super(body?.detail ?? `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

export interface AuthState {
  user: AuthUser | null
  /** True until the first refresh attempt settles — otherwise every app flashes signed-out. */
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  /**
   * End the session.
   *
   * **Reaches the server**, because a refresh token is now a row that can be revoked rather
   * than a JWT that is valid until it expires. Forgetting it locally would leave a live
   * session behind on a shared computer.
   *
   * Returns a promise, but a caller need not await it: the local state is cleared first, so
   * the person is signed out of the page whatever the network does.
   */
  signOut: () => Promise<void>
  /**
   * Adopt a token pair obtained some other way than signing in.
   *
   * One caller: accepting an invitation, which creates the account and returns the same pair
   * a sign-in does. Making somebody type the password they have just chosen into a login
   * form is a step that exists only to prove the software works.
   *
   * Not a back door — the tokens still came from the API, and this only stores what it was
   * handed and loads the user behind them.
   */
  adoptSession: (tokens: AuthTokens) => Promise<void>
  /**
   * Read the signed-in person again, from `GET /auth/me`.
   *
   * **For after they have edited themselves.** The user is loaded once, when a session is
   * resumed or begun, and the chrome reads it — so somebody who corrects their own name on
   * a profile screen is still greeted by the old one until they reload the page, which reads
   * as the save not having worked.
   *
   * The API's answer, never what the form sent: the same rule every screen here follows, and
   * the reason a failed reload signs the session out rather than keeping a stale user.
   */
  reloadUser: () => Promise<void>
  /** A fetch that carries the access token and refreshes once on a 401. */
  request: <T>(path: string, init?: RequestInit) => Promise<T>
  /** Whether the signed-in person holds any of these roles. Presentation only — see below. */
  hasRole: (...roles: string[]) => boolean
}

/**
 * How somebody is named in a header, a menu, or anywhere the chrome greets them.
 *
 * The address when there is no name, because **most accounts carry no name**: one created by
 * accepting an invitation carries whatever was typed on the form, and one made by the
 * bootstrap script carries whatever `--name` said, which is often nothing. "Signed in as"
 * followed by a blank is worse than an address.
 *
 * **Shared at the second caller** — Steward's topbar had it, Partners' account menu
 * needed the same three lines, and three lines is exactly the size at which copying stops
 * feeling like a decision. Ground rule 5.
 */
export function accountName(user: AuthUser): string {
  const named = [user.name, user.last_name].filter(Boolean).join(' ').trim()
  return named || user.identities?.[0]?.value || ''
}
